/**
 * Skeleton components for auth loading states
 */

import React from 'react';
import { InlineLoader, SectionLoader } from './progressive-loading';
import { Skeleton } from './skeleton';

export const AuthSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <InlineLoader 
        variant="spinner" 
        size="lg"
      />
    </div>
  );
};

export const DataSkeleton: React.FC<{ message?: string }> = ({ 
  message = "Loading your data..." 
}) => {
  return (
    <div className="p-8">
      <SectionLoader variant="minimal" className="py-8" />
    </div>
  );
};

export const SidebarSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 p-4">
      {/* User section skeleton */}
      <div className="flex items-center space-x-3 opacity-0 animate-[fade-in_0.3s_ease-out_0.1s_forwards]">
        <Skeleton variant="soft" className="w-10 h-10 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton variant="default" className="h-4 w-24" />
          <Skeleton variant="soft" className="h-3 w-16" />
        </div>
      </div>
      
      {/* Navigation skeleton */}
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton 
            key={i} 
            variant="default"
            className="h-10 opacity-0 animate-[fade-in_0.3s_ease-out_forwards]"
            style={{ animationDelay: `${0.15 + i * 0.05}s` }}
          />
        ))}
      </div>
      
      {/* Company selector skeleton */}
      <div className="pt-4">
        <Skeleton 
          variant="accent" 
          className="h-12 opacity-0 animate-[fade-in_0.3s_ease-out_0.4s_forwards]"
        />
      </div>
    </div>
  );
};

export const WorkspaceSkeleton: React.FC = () => {
  return (
    <div className="space-y-6 p-6 md:p-8">
      {/* Header skeleton */}
      <div className="space-y-3 opacity-0 animate-[fade-in_0.3s_ease-out_0.1s_forwards]">
        <Skeleton variant="default" className="h-9 w-64 max-w-full" />
        <Skeleton variant="soft" className="h-5 w-96 max-w-full" />
      </div>
      
      {/* Content skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div 
            key={i} 
            className="p-5 rounded-2xl shadow-sm backdrop-blur-sm bg-gradient-to-br from-card/50 to-muted/20 space-y-3 opacity-0 animate-[fade-in_0.3s_ease-out_forwards]"
            style={{ animationDelay: `${0.2 + i * 0.05}s` }}
          >
            <Skeleton variant="accent" className="h-6 w-40" />
            <Skeleton variant="default" className="h-4 w-full" />
            <Skeleton variant="default" className="h-4 w-full" />
            <Skeleton variant="soft" className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
};